import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useGraphQLClient, useSubscribe } from "@package/graphql/react";

import type { ConditionalFormattingRulesFragmentFragment } from "../../__generated__/gql/graphql";
import { graphql, readFragment } from "../../graphql";

import { createEntityQueryHook } from "../../utils/query";
import { useUserId } from "../../context/auth/hooks";

export type TConditionalFormattingRule = Omit<
  ConditionalFormattingRulesFragmentFragment,
  "$fragmentName" | "__typename" | "rule"
> & {
  rule: "equals" | "isNotEqual" | "lessThan" | "moreThan" | "lessThanOrEqual" | "moreThanOrEqual";
};

export type TConditionalFormattingRuleOptions = Pick<
  TConditionalFormattingRule,
  "bg_color" | "rule" | "limit" | "note" | "stop_if_true" | "invert_text_color" | "bold_text"
>;

export const useConditionalFormattingCache = createEntityQueryHook<TConditionalFormattingRule[], [pageId: string]>(
  (pageId) => ["conditional_formatting_rules", useUserId(), pageId],
);

const ruleLocaleCollator = new Intl.Collator();
export const sortRules = (rules: TConditionalFormattingRule[]) =>
  [...rules].sort((a, b) => ruleLocaleCollator.compare(b.priority_idx, a.priority_idx));

const useConditionalFormattingRulesQueryOptions = (pageId: string) => {
  const graphql = useGraphQLClient();
  const cache = useConditionalFormattingCache(pageId);
  return queryOptions({
    queryKey: cache.queryKey,
    queryFn: () =>
      graphql
        .execute(ConditionalFormattingRulesByPageId, { pageId })
        .then(
          (x) =>
            (readFragment(ConditionalFormattingRulesFragment, x.data?.conditional_formatting_rules) ??
              []) as TConditionalFormattingRule[],
        ),
  });
};

export const useSyncConditionalFormattingRules = (pageId: string) => {
  const cache = useConditionalFormattingCache(pageId);

  useSubscribe({
    document: ConditionalFormattingSub,
    variables: { pageId },
    next: (res) =>
      cache.set(
        readFragment(
          ConditionalFormattingRulesFragment,
          res.data?.conditional_formatting_rules ?? [],
        ) as TConditionalFormattingRule[],
      ),
  });
};

export const useQueryConditionalFormattingRules = (pageId: string) =>
  useQuery({
    ...useConditionalFormattingRulesQueryOptions(pageId),
    select: (x) => sortRules(x),
  });

export const useQueryConditionalFormattingRulesGrouped = (pageId: string) =>
  useQuery({
    ...useConditionalFormattingRulesQueryOptions(pageId),
    select: groupRules,
  });

export const useInsertConditionalFormattingRule = (pageId: string) => {
  const graphql = useGraphQLClient();
  const cache = useConditionalFormattingCache(pageId);
  return useMutation({
    mutationFn: (rule: Omit<TConditionalFormattingRule, "id">) =>
      graphql.execute(CreateConditionalFormattingRules, { rule }),
    onSettled: () => cache.invalidate(),
  });
};

export const useDeleteConditionalFormattingRules = (pageId: string) => {
  const cache = useConditionalFormattingCache(pageId);
  const graphql = useGraphQLClient();
  return useMutation({
    mutationFn: (ids: string[]) => graphql.execute(DeleteConditionalFormattingRules, { ids }),
    onMutate: async (ids) => cache.set((old) => old?.filter((x) => !ids.includes(x.id))),
    onSettled: () => cache.invalidate(),
  });
};

// Create an object where the key is `columnId-rowId` and the value is an array of rules based on the ones that have the same columnId and rowId.
const groupRules = (rules: TConditionalFormattingRule[]) =>
  rules.reduce((acc, rule) => {
    const key = `${rule.column_id}-${rule.row_id}`;
    if (!acc[key]) return Object.assign(acc, { [key]: [rule] });
    return Object.assign(acc, { [key]: [...acc[key], rule] });
  }, {} as TConditionalFormattingRulesGrouped);

export type TConditionalFormattingRulesGrouped = Record<string, TConditionalFormattingRule[]>;

export const useUpsertConditionalFormattingRules = (pageId: string) => {
  const graphql = useGraphQLClient();
  const cache = useConditionalFormattingCache(pageId);
  return useMutation({
    mutationFn: (rules: TConditionalFormattingRule[]) =>
      graphql.execute(UpsertConditionalFormattingRules, { objects: rules }),
    onMutate: (rules) => {
      cache.set((old) => {
        const newRules = rules.filter((x) => !old?.find((y) => y.id === x.id));
        return [
          ...(old ?? []).map((x) => {
            const newRule = rules.find((y) => y.id === x.id);
            if (newRule) return newRule;
            return x;
          }),
          ...newRules,
        ];
      });
    },
    onSettled: () => cache.invalidate(),
  });
};

const UpsertConditionalFormattingRules = graphql(`
  mutation UpsertConditionalFormattingRules(
    $objects: [conditional_formatting_rules_insert_input!]!
  ) {
    insert_conditional_formatting_rules(
      objects: $objects
      on_conflict: {
        constraint: conditional_formatting_rules_pkey
        update_columns: [
          _id
          bg_color
          bold_text
          column_id
          invert_text_color
          limit
          note
          page_id
          priority_idx
          row_id
          rule
          stop_if_true
          user_id
        ]
      }
    ) {
      affected_rows
    }
  }
`);

export const ConditionalFormattingRulesFragment = graphql(`
  fragment ConditionalFormattingRulesFragment on conditional_formatting_rules {
    id
    _id
    bg_color
    bold_text
    column_id
    invert_text_color
    limit
    note
    page_id
    priority_idx
    row_id
    rule
    stop_if_true
    user_id
  }
`);

export const ConditionalFormattingQuery = graphql(`
	query ConditionalFormattingQuery($pageIds: [String!]!) {
		conditional_formatting_rules(where: { page_id: { _in: $pageIds } }) {
			...ConditionalFormattingRulesFragment
		}
	}
`);

export const ConditionalFormattingSub = graphql(`
	subscription ConditionalFormattingSub($pageId: String!) {
		conditional_formatting_rules(where: { page_id: { _eq: $pageId } }) {
			...ConditionalFormattingRulesFragment
		}
	}
`);

export const ConditionalFormattingInsertMany = graphql(`
  mutation ConditionalFormattingInsertMany($objects: [conditional_formatting_rules_insert_input!]!) {
    insert_conditional_formatting_rules(objects: $objects) {
      affected_rows
    }
  }
`);

const CreateConditionalFormattingRules = graphql(`
  mutation Insert_conditional_formatting_rules_one(
    $rule: conditional_formatting_rules_insert_input!
  ) {
    insert_conditional_formatting_rules_one(object: $rule) {
      id
    }
  }
`);

const ConditionalFormattingRulesByPageId = graphql(`
	query ConditionalFormattingRulesByPageId($pageId: String!) {
		conditional_formatting_rules(
			where: { page_id: { _eq: $pageId } }
			order_by: { priority_idx: asc }
		) {
			...ConditionalFormattingRulesFragment
		}
	}
`);

const DeleteConditionalFormattingRules = graphql(`
  mutation DeleteConditionalFormattingRules($ids: [String!]!) {
    delete_conditional_formatting_rules(where: { id: { _in: $ids }}) {
      affected_rows
    }
  }
`);
